import React, { useState, useEffect } from 'react';
import { Link, useParams, useHistory } from 'react-router-dom';
import { Card, Table, Button, Form, Row, Col, InputGroup, Dropdown, Badge, Spinner, Modal, Container } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faSearch, faEye, faEdit, faTrashAlt, faEllipsisV, faSortUp, faSortDown, faSync } from '@fortawesome/free-solid-svg-icons';
import { getVariantOptionList, deleteVariantOption } from '../../Controller/VariantOptionController';
import ConfirmationAlert from '../../../../../GlobalModules/Utility/Components/ConfirmationAlert';
import Utility from '../../../../../GlobalModules/Utility/Utility';
import { DebounceInput } from 'react-debounce-input';
import Pagination from '../../../../../GlobalModules/Utility/Components/Pagination';
import { connect } from 'react-redux';
import { withNamespaces } from 'react-i18next';
import { loaderStateTrue, loaderStateFalse, handleActiveLink } from '../../../../../Actions/AllAction';

/**
 * Component for displaying and managing variant options list
 * @returns {JSX.Element} VariantOptionList component
 */
const VariantOptionList = (props) => {
  const { lng } = useParams();
  const history = useHistory();

  // State for variant options data
  const [variantOptions, setVariantOptions] = useState([]);
  const [totalItems, setTotalItems] = useState(0);

  // State for search, filters and pagination
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(
    parseInt(localStorage.getItem('variant_option_page_size')) || 10
  );

  // State for sorting
  const [sortField, setSortField] = useState('id');
  const [sortDirection, setSortDirection] = useState('desc');

  // Consolidated filters state for API calls
  const [filters, setFilters] = useState({
    limit: parseInt(localStorage.getItem('variant_option_page_size')) || 10,
    offset: 0,
    search: '',
    status: '',
    sort: [{
      field: 'id',
      sort: 'desc'
    }]
  });

  // Alert state for delete confirmation
  const [showDeleteAlert, setShowDeleteAlert] = useState(false);
  const [deleteItemSlack, setDeleteItemSlack] = useState(null);
  const [deleteItemName, setDeleteItemName] = useState('');

  // Fetch variant options on component mount and when filters change
  useEffect(() => {
    fetchVariantOptions();
  }, [filters]);

  const handleRefresh = () => {
    fetchVariantOptions();
  };

  /**
   * Fetch variant options with current filters
   */
  const fetchVariantOptions = async () => {
    props.loaderStateTrue();
    try {
      const response = await getVariantOptionList(filters);

      if (response.success) {
        setVariantOptions(response.data || []);
        setTotalItems(response.total || 0);
      } else {
        Utility.toastNotifications(response.message || 'Failed to fetch variant options', 'Error', 'error');
        setVariantOptions([]);
      }
    } catch (error) {
      console.error('Error fetching variant options:', error);
      Utility.toastNotifications('An error occurred while fetching variant options', 'Error', 'error');
      setVariantOptions([]);
    } finally {
      props.loaderStateFalse();
    }
  };

  /**
   * Handle search input change with debounce built into DebounceInput
   */
  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setCurrentPage(1); // Reset to first page on new search
    setFilters(prev => ({
      ...prev,
      search: value,
      offset: 0 // Reset offset when search changes
    }));
  };

  /**
   * Handle status filter change
   */
  const handleStatusFilterChange = (e) => {
    const value = e.target.value;
    setStatusFilter(value);
    setCurrentPage(1); // Reset to first page on filter change
    setFilters(prev => ({
      ...prev,
      status: value,
      offset: 0 // Reset offset when filter changes
    }));
  };

  /**
   * Handle column sort
   * @param {string} field - Field to sort by
   */
  const handleSort = (field) => {
    // If clicking the same field, toggle sort direction
    if (sortField === field) {
      const newDirection = sortDirection === 'asc' ? 'desc' : 'asc';
      setSortDirection(newDirection);
      setFilters(prev => ({
        ...prev,
        sort: [{
          field,
          sort: newDirection
        }]
      }));
    } else {
      // If clicking a new field, default to ascending sort
      setSortField(field);
      setSortDirection('asc');
      setFilters(prev => ({
        ...prev,
        sort: [{
          field,
          sort: 'asc'
        }]
      }));
    }
  };

  /**
   * Initialize delete confirmation for a variant option
   * @param {Object} variantOption - Variant option to delete
   */
  const confirmDelete = (variantOption) => {
    setDeleteItemSlack(variantOption.slack);
    setDeleteItemName(variantOption.label || variantOption.variantOptionCode);
    setShowDeleteAlert(true);
  };

  /**
   * Cancel delete operation
   */
  const cancelDelete = () => {
    setShowDeleteAlert(false);
    setDeleteItemSlack(null);
    setDeleteItemName('');
  };

  /**
   * Execute delete operation after confirmation
   */
  const handleDelete = async () => {
    if (!deleteItemSlack) {
      return;
    }

    try {
      const response = await deleteVariantOption(deleteItemSlack);

      if (response.success) {
        Utility.toastNotifications(response.message || 'Variant option deleted successfully', 'Success', 'success');
        fetchVariantOptions(); // Refresh list after delete
      } else {
        Utility.toastNotifications(response.message || 'Failed to delete variant option', 'Error', 'error');
      }
    } catch (error) {
      console.error('Error deleting variant option:', error);
      Utility.toastNotifications('An error occurred while deleting variant option', 'Error', 'error');
    } finally {
      setShowDeleteAlert(false);
      setDeleteItemSlack(null);
      setDeleteItemName('');
    }
  };

  /**
   * Handle page change
   * @param {number} page - Page number to navigate to
   */
  const handlePageChange = (page) => {
    setCurrentPage(page);
    setFilters(prev => ({
      ...prev,
      offset: (page - 1) * itemsPerPage
    }));
  };

  /**
   * Handle limit change for pagination
   */
  const handleLimitChange = (limit) => {
    const newLimit = parseInt(limit);
    setItemsPerPage(newLimit);
    localStorage.setItem('variant_option_page_size', newLimit);
    setFilters(prev => ({
      ...prev,
      limit: newLimit,
      offset: 0 // Reset to first page when changing limit
    }));
  };

  //======= add action permission ====//
  // Check multiple permissions at once
  const variantOptionPermissions = Utility.hasMultipleActionPermissions(
    'settings',
    'variant_options',
    ['view', 'create', 'edit', 'delete', 'export', 'approve', 'receive', 'verify', 'reject', 'verify_accept'],
    props?.userCredentials?.user_details?.user_type || ['admin', 'app_admin']
  );

  // Check if any actions are available for display in the table
  const hamburgerTableActions = variantOptionPermissions.view || variantOptionPermissions.edit || variantOptionPermissions.delete;

  return (
    <Container fluid>
      <div className="page-header top-header-box columnList">
        <div className="page-title" style={{ flexDirection: 'column', alignItems: 'flex-start' }}>
          <h4>Variant Options</h4>
          <h6 style={{ margin: 0 }}>Manage your variant options</h6>
        </div>
        <div className="page-btn">
          {variantOptionPermissions.create && (
            <Link to={`/${lng}/settings/variant-options/add`} className="btn btn-primary btn-added">
              <FontAwesomeIcon icon={faPlus} className="me-1" /> Add Variant Option
            </Link>
          )}
        </div>
      </div>

      <Card>
        <Card.Body>

          <Row className="mb-0 d-flex justify-content-end">
            <Col md={3} className='mb-3'>
              <InputGroup className='input-group input-group-sm'>
                <InputGroup.Text>
                  <FontAwesomeIcon icon={faSearch} />
                </InputGroup.Text>
                <DebounceInput
                  minLength={2}
                  debounceTimeout={500}
                  className="form-control"
                  placeholder="Search variant options..."
                  value={searchTerm}
                  onChange={(e) => handleSearch(e)}
                />
                <div className='ms-2'>
                  <Button
                    variant='outline-secondary'
                    size='sm'
                    onClick={handleRefresh}
                    disabled={props.loaderState}
                    title='Refresh'
                  >
                    <FontAwesomeIcon
                      icon={faSync}
                      spin={props.loaderState}
                    />
                  </Button>
                </div>
              </InputGroup>
            </Col>
          </Row>

          <div className="table-responsive">
            <Table className="table-striped">
              <thead>
                <tr>
                  <th onClick={() => handleSort('variantOptionCode')} style={{ cursor: 'pointer' }}>
                    Code {sortField === 'variantOptionCode' && <FontAwesomeIcon icon={sortDirection === 'asc' ? faSortUp : faSortDown} size="xs" />}
                  </th>
                  <th onClick={() => handleSort('label')} style={{ cursor: 'pointer' }}>
                    Label {sortField === 'label' && <FontAwesomeIcon icon={sortDirection === 'asc' ? faSortUp : faSortDown} size="xs" />}
                  </th>
                  <th onClick={() => handleSort('status')} style={{ cursor: 'pointer' }}>
                    Status {sortField === 'status' && <FontAwesomeIcon icon={sortDirection === 'asc' ? faSortUp : faSortDown} size="xs" />}
                  </th>
                  <th onClick={() => handleSort('createdAt')} style={{ cursor: 'pointer' }}>
                    Created On {sortField === 'createdAt' && <FontAwesomeIcon icon={sortDirection === 'asc' ? faSortUp : faSortDown} size="xs" />}
                  </th>
                  {hamburgerTableActions && (
                    <th className="">Action</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {props.loaderState ? (
                  <tr>
                    <td colSpan={hamburgerTableActions ? 6 : 5} className="text-center py-4">
                      {/* <Spinner animation="border" role="status" variant="primary">
                        <span className="visually-hidden">Loading...</span>
                      </Spinner>
                      <p className="mt-2">Loading variant options...</p> */}
                    </td>
                  </tr>
                ) : variantOptions.length === 0 ? (
                  <tr>
                    <td colSpan={hamburgerTableActions ? 6 : 5} className="text-center py-5 nosearchResult">
                      No variant options found
                      {searchTerm && (
                        <div className="mt-2">
                          <Button variant="link" onClick={() => {
                            setSearchTerm('');
                            setFilters(prev => ({ ...prev, search: '' }));
                          }}>
                            Clear search
                          </Button>
                        </div>
                      )}
                    </td>
                  </tr>
                ) : (
                  variantOptions.map((option) => (
                    <tr
                      key={option.slack}
                      style={{ cursor: 'pointer' }}
                      onDoubleClick={() => history.push(`/${lng}/settings/variant-options/view/${option.slack}`)}
                    >
                      <td>{option.variantOptionCode}</td>
                      <td>{option.label}</td>
                      <td>

                        <Badge
                          bg={option.status == 1 ? 'success' : 'secondary'}
                        // style={{ cursor: 'pointer' }}
                        >
                          {option.status == 1 ? 'Active' : 'Inactive'}
                        </Badge>
                      </td>
                      <td>{Utility.get12HourFormat(option.createdAt)}</td>
                      {hamburgerTableActions && (
                        <td className="text-end">
                          <Dropdown align="end">
                            <Dropdown.Toggle variant="light" size="sm" id={`dropdown-${option.id}`} className="btn-icon no-caret">
                              <FontAwesomeIcon icon={faEllipsisV} />
                            </Dropdown.Toggle>
                            <Dropdown.Menu>
                              {/* {variantOptionPermissions.view && ( */}
                                <Dropdown.Item as={Link} to={`/${lng}/settings/variant-options/view/${option.slack}`}>
                                  <FontAwesomeIcon icon={faEye} className="me-2" />
                                  View
                                </Dropdown.Item>
                              {/* )} */}
                              {variantOptionPermissions.edit && (
                                <Dropdown.Item as={Link} to={`/${lng}/settings/variant-options/edit/${option.slack}`}>
                                  <FontAwesomeIcon icon={faEdit} className="me-2" />
                                  Edit
                                </Dropdown.Item>
                              )}
                              {variantOptionPermissions.delete && (
                                <>
                                  <Dropdown.Divider />
                                  <Dropdown.Item
                                    onClick={() => confirmDelete(option)}
                                    className="text-danger"
                                  >
                                    <FontAwesomeIcon icon={faTrashAlt} className="me-2" />
                                    Delete
                                  </Dropdown.Item>
                                </>
                              )}
                            </Dropdown.Menu>
                          </Dropdown>
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>

          {/* Pagination */}
          <Pagination
            totalItems={totalItems}
            itemsPerPage={itemsPerPage}
            currentPage={currentPage}
            onPageChange={handlePageChange}
            siblingCount={2}
            onLimitChange={handleLimitChange}
            limitOptions={[10, 25, 50, 100, 500, 1000]}
          />
        </Card.Body>
      </Card>

      {/* Delete Confirmation Alert */}
      {showDeleteAlert && (
        <Modal show={showDeleteAlert} onHide={cancelDelete} centered>
          <Modal.Header closeButton>
            <Modal.Title>Confirmation</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <ConfirmationAlert
              BodyFirstContent="Are you sure you want to delete variant option:"
              BodySecondContent={deleteItemName}
              BodyThirdContent="?"
              BodyForthContent="This action cannot be undone."
              confirmationButtonContent="Yes, delete it!"
              cancelButtonContent="Cancel"
              deleteIconVissable={true}
              deleteConfirmButton={handleDelete}
              deleteCancleButton={cancelDelete}
            />
          </Modal.Body>
        </Modal>
      )}
    </Container>
  );
};

const mapStateToProps = (globalState) => {
  return {
    userCredentials: globalState.LoginReducer.userCredentials,
    token: globalState.LoginReducer.token,
    loaderState: globalState.mainReducerData.loaderState
  };
}

export default connect(mapStateToProps, { loaderStateTrue, loaderStateFalse, handleActiveLink })(withNamespaces()(VariantOptionList));

